import React, { useState, useEffect } from 'react';
import { X, Save, Database, Eye, Edit2 } from 'lucide-react';
import { dataMappingAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface DataSourceProviderViewEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  dataSource: any | null;
  mode: 'view' | 'edit';
}

const DataSourceProviderViewEditModal: React.FC<DataSourceProviderViewEditModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  dataSource,
  mode: initialMode,
}) => {
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
  const [formData, setFormData] = useState({
    data_source_id: '',
    data_sources_id: '',
    data_sources_description: '',
    data_source_contact_name: '',
    data_source_email: '',
    data_source_portal: '',
    project_name: '',
    deidentify_or_not: '',
    clinical_or_preclinical: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load data source data when modal opens
  useEffect(() => {
    if (isOpen && dataSource) {
      // Use dataSource.id (from data_sources table) as data_source_id for the mapping
      // This is the foreign key that links to data_sources.id
      setFormData({
        data_source_id: dataSource.id?.toString() || dataSource.data_source_id?.toString() || '',
        data_sources_id: dataSource.data_sources_id || dataSource.Data_Sources_ID || '',
        data_sources_description: dataSource.data_sources_description || dataSource.Data_Sources_Description || '',
        data_source_contact_name: dataSource.contact_name || dataSource.data_source_contact_name || dataSource.Data_Source_Contact_Name || '',
        data_source_email: dataSource.contact_email || dataSource.data_source_email || dataSource.Data_Source_Email || '',
        data_source_portal: dataSource.portal_url || dataSource.data_source_portal || dataSource.Data_Source_Portal || '',
        project_name: dataSource.project_name || dataSource.Project_Name || '',
        deidentify_or_not: dataSource.deidentified || dataSource.deidentify_or_not || dataSource.Deidentify_or_Not || '',
        clinical_or_preclinical: dataSource.clinical_type || dataSource.clinical_or_preclinical || dataSource.Clinical_or_Preclinical || '',
      });
      setMode(initialMode);
    }
  }, [isOpen, dataSource, initialMode]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      // Ensure data_source_id is a number if it exists
      const submitData = {
        ...formData,
        data_source_id: formData.data_source_id ? parseInt(formData.data_source_id, 10) : null,
      };
      
      await dataMappingAPI.createDataSourceMapping(submitData);
      toast.success('Data source provider updated successfully');
      onClose();
      onSuccess();
    } catch (error: any) {
      const apiErrors = error?.response?.data?.errors as Array<{ msg: string; param?: string }> | undefined;
      if (apiErrors && apiErrors.length) {
        const fieldErrors: Record<string, string> = {};
        apiErrors.forEach(e => {
          if (e.param) fieldErrors[e.param] = e.msg;
        });
        setErrors(fieldErrors);
        toast.error('Please fix the highlighted fields.');
      } else {
        toast.error(error.response?.data?.error || 'Failed to update data source provider');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !dataSource) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Database className="h-6 w-6 text-[var(--accent)]" />
            <h2 className="text-xl font-semibold text-gray-900">
              {mode === 'view' ? 'View Data Source Provider' : 'Edit Data Source Provider'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {mode === 'view' && (
              <button
                onClick={() => setMode('edit')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Edit"
              >
                <Edit2 className="h-5 w-5 text-gray-600" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {mode === 'view' ? (
            // View Mode
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data_Sources_ID</label>
                  <p className="text-sm text-gray-900 font-mono">{formData.data_sources_id || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data_Sources_Description</label>
                  <p className="text-sm text-gray-900">{formData.data_sources_description || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                  <p className="text-sm text-gray-900">{formData.data_source_contact_name || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="text-sm text-gray-900">{formData.data_source_email || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Portal</label>
                  <p className="text-sm text-gray-900">{formData.data_source_portal || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Clinical/Preclinical</label>
                  <p className="text-sm text-gray-900">{formData.clinical_or_preclinical || '-'}</p>
                </div>
              </div>
              {formData.project_name && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                  <p className="text-sm text-gray-900">{formData.project_name}</p>
                </div>
              )}
            </div>
          ) : (
            // Edit Mode
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="data_sources_id" className="block text-sm font-medium text-gray-700 mb-2">
                    Data_Sources_ID
                  </label>
                  <input
                    type="text"
                    id="data_sources_id"
                    value={formData.data_sources_id}
                    onChange={(e) => handleInputChange('data_sources_id', e.target.value)}
                    placeholder="D001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
                <div>
                  <label htmlFor="data_sources_description" className="block text-sm font-medium text-gray-700 mb-2">
                    Data_Sources_Description
                  </label>
                  <input
                    type="text"
                    id="data_sources_description"
                    value={formData.data_sources_description}
                    onChange={(e) => handleInputChange('data_sources_description', e.target.value)}
                    placeholder="Institution | Team/Lab"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
                <div>
                  <label htmlFor="data_source_contact_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    id="data_source_contact_name"
                    value={formData.data_source_contact_name}
                    onChange={(e) => handleInputChange('data_source_contact_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
                <div>
                  <label htmlFor="data_source_email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="data_source_email"
                    value={formData.data_source_email}
                    onChange={(e) => handleInputChange('data_source_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
                <div>
                  <label htmlFor="data_source_portal" className="block text-sm font-medium text-gray-700 mb-2">
                    Portal
                  </label>
                  <input
                    type="text"
                    id="data_source_portal"
                    value={formData.data_source_portal}
                    onChange={(e) => handleInputChange('data_source_portal', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
                <div>
                  <label htmlFor="clinical_or_preclinical" className="block text-sm font-medium text-gray-700 mb-2">
                    Clinical/Preclinical
                  </label>
                  <select
                    id="clinical_or_preclinical"
                    value={formData.clinical_or_preclinical}
                    onChange={(e) => handleInputChange('clinical_or_preclinical', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  >
                    <option value="">Select...</option>
                    <option value="Clinical">Clinical</option>
                    <option value="Preclinical">Preclinical</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="deidentify_or_not" className="block text-sm font-medium text-gray-700 mb-2">
                    Deidentify or Not
                  </label>
                  <select
                    id="deidentify_or_not"
                    value={formData.deidentify_or_not}
                    onChange={(e) => handleInputChange('deidentify_or_not', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="project_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Project Name
                  </label>
                  <textarea
                    id="project_name"
                    value={formData.project_name}
                    onChange={(e) => handleInputChange('project_name', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                    placeholder="Multiple projects separated by semicolons"
                  />
                </div>
              </div>

              {/* Submit buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    if (initialMode === 'view') {
                      setMode('view');
                    } else {
                      onClose();
                    }
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                >
                  {initialMode === 'view' ? 'Cancel' : 'Close'}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataSourceProviderViewEditModal;
